import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import OpalCard from '../../../../../opal/OpalCard'
import InlineCardHeader from '../../../../../molecules/Card/InlineCardHeader'
import SydButton from '../../../../../commonDesign/Button'
import SydLabel from '../../../../../commonDesign/SydLabel'
import SydSelect from '../../../../../commonDesign/SydSelect'
import FieldDisplay from '../../../../../molecules/FieldDisplay'

function VersionControlCard ({
  blueprint,
  allVersions,
  selectedVersion,
  onSelectVersion,
  onSetActive,
  onAddDefinition
}) {
  const activeVersionId = blueprint.definitionVersionId
  const workingVersionId = selectedVersion?.definitionVersionId || activeVersionId
  const isActive = workingVersionId === activeVersionId

  const versionOptions = useMemo(() => {
    // Just list all versions once, showing which is active
    return (allVersions || []).map(v => {
      const isActiveVer = v.definitionVersionId === activeVersionId
      const dateStr = v.versionDate ? new Date(v.versionDate).toLocaleDateString() : ''
      const descStr = v.description ? ` - ${v.description}` : ''
      const activeLabel = isActiveVer ? ' (active)' : ''

      return {
        label: `${v.definitionVersionId}${activeLabel}${descStr}${dateStr ? ` • ${dateStr}` : ''}`,
        value: v.definitionVersionId // Use version ID string as value
      }
    })
  }, [allVersions, activeVersionId])

  // Current selected value (version ID for matching in select)
  const selectedValue = selectedVersion?.definitionVersionId || activeVersionId

  const fields = useMemo(() => {
    const purposeCount = selectedVersion?.data?.purposes
      ? Object.keys(selectedVersion.data.purposes).length
      : (blueprint.definition?.data?.purposes ? Object.keys(blueprint.definition.data.purposes).length : 0)

    return [
      { label: 'Working Version', value: workingVersionId || 'None' },
      { label: 'Active Version', value: activeVersionId || 'None' },
      { label: 'Status', value: isActive ? 'Active (Production)' : 'Draft' },
      { label: 'Purposes', value: purposeCount }
    ]
  }, [workingVersionId, activeVersionId, isActive, selectedVersion, blueprint])

  return (
    <OpalCard size='sm'>
      <InlineCardHeader
        action={<SydButton onClick={onAddDefinition} icon='add'>New Version</SydButton>}
      >
        Version Control
      </InlineCardHeader>

      <div style={{ padding: '16px' }}>
        {/* Version Selector */}
        <div style={{ marginBottom: '16px' }}>
          <SydLabel label='Working Version'>
            <SydSelect
              options={versionOptions}
              value={selectedValue}
              onChange={e => {
                // Find the version object by ID, or null for active
                const versionId = e.target.value
                if (versionId === activeVersionId) {
                  onSelectVersion(null) // Active version
                } else {
                  const version = allVersions.find(v => v.definitionVersionId === versionId)
                  onSelectVersion(version)
                }
              }}
            />
          </SydLabel>
        </div>

        {/* Active Version Warning */}
        {isActive && (
          <div style={{
            padding: '8px',
            backgroundColor: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '4px',
            marginBottom: '12px',
            fontSize: '0.875rem'
          }}
          >
            <strong>⚠ You are editing the active version</strong>
            <br />
            Changes will affect production immediately when saved.
          </div>
        )}

        {/* Version Info */}
        <FieldDisplay fields={fields} />

        {/* Set Active Button */}
        {!isActive && workingVersionId && (
          <div style={{ marginTop: '12px' }}>
            <SydButton
              variant='primary'
              size='sm'
              onClick={() => onSetActive(selectedVersion)}
            >
              Set as Active Version
            </SydButton>
          </div>
        )}
      </div>
    </OpalCard>
  )
}

VersionControlCard.propTypes = {
  blueprint: PropTypes.object.isRequired,
  allVersions: PropTypes.array,
  selectedVersion: PropTypes.object,
  onSelectVersion: PropTypes.func.isRequired,
  onSetActive: PropTypes.func.isRequired,
  onAddDefinition: PropTypes.func.isRequired
}

VersionControlCard.defaultProps = {
  allVersions: [],
  selectedVersion: null
}

export default VersionControlCard
